<template>
    <div class="location-selector">
        <div class="title">选择门店</div>
        <div class="search-bar">
            <Input v-model="keywords" placeholder="输入门店名称或地址" @on-enter="search" />
            <Button :disabled="!ready || searching" :loading="searching" @click="search">搜索</Button>
        </div>
        <div v-if="error" class="map-message" role="alert">
            {{ error }} <Button v-if="!ready" size="small" @click="initMap">重新加载</Button>
        </div>
        <div v-if="loading" class="map-message">地图加载中…</div>
        <div ref="map" class="map-canvas"></div>
        <div v-if="results.length" class="search-results">
            <button v-for="(item, index) in results" :key="index" type="button" @click="selectResult(item)">
                <strong>{{ item.title }}</strong><span>{{ item.address }}</span>
            </button>
        </div>
        <div class="hint">搜索后选择具体门店，也可点击地图或拖动标记调整位置。</div>
        <div class="foot">
            <Button @click="$emit('cancel')">取消</Button>
            <Button type="primary" :disabled="!ready || searching || !location" @click="locationConfirm">确定</Button>
        </div>
    </div>
</template>
<script>
import { loadBaiduMap, gcjToBd, bdToGcj, validLocation } from '@/utils/baiduMap'

export default {
    props: { current: { type: Array, default: null } },
    data() {
        return { keywords: '', loading: false, ready: false, searching: false, error: '', results: [], location: null }
    },
    mounted() { this.initMap() },
    beforeDestroy() {
        this.disposed = true
        clearTimeout(this.searchTimer)
        if (this.localSearch) this.localSearch.clearResults()
        if (this.map) this.map.clearOverlays()
        this.map = null
        this.marker = null
    },
    watch: {
        current(value) {
            if (this.ready && validLocation(value)) this.setBusinessLocation(value)
        },
    },
    methods: {
        async initMap() {
            if (this.loading || this.ready) return
            this.loading = true
            this.error = ''
            try {
                const BMap = await loadBaiduMap()
                if (this.disposed) return
                this.map = new BMap.Map(this.$refs.map)
                this.map.enableScrollWheelZoom(true)
                this.map.addControl(new BMap.NavigationControl())
                this.setBusinessLocation(validLocation(this.current) ? this.current : [104.065778, 30.657487])
                this.map.addEventListener('click', event => this.selectPoint(event.point))
                this.ready = true
            } catch (error) {
                if (!this.disposed) this.error = error.message
            } finally {
                if (!this.disposed) this.loading = false
            }
        },
        setBusinessLocation(value) {
            const coordinates = gcjToBd(value)
            this.location = value.slice() // Opening and confirming must not perturb an existing location.
            this.showPoint(new window.BMap.Point(coordinates[0], coordinates[1]))
        },
        showPoint(point) {
            this.map.centerAndZoom(point, 16)
            if (!this.marker) {
                this.marker = new window.BMap.Marker(point)
                this.map.addOverlay(this.marker)
                this.marker.enableDragging()
                this.marker.addEventListener('dragend', event => this.selectPoint(event.point))
            } else this.marker.setPosition(point)
        },
        selectPoint(point) {
            if (!point || !validLocation([point.lng, point.lat])) return
            this.location = bdToGcj([point.lng, point.lat])
            this.showPoint(point)
            this.error = ''
        },
        selectResult(item) { this.selectPoint(item.point) },
        search() {
            if (!this.ready || this.searching || !this.keywords.trim()) return
            this.error = ''
            this.results = []
            this.searching = true
            const search = new window.BMap.LocalSearch(this.map, {
                pageCapacity: 10,
                onSearchComplete: result => {
                    if (this.disposed || this.localSearch !== search || !this.searching) return
                    clearTimeout(this.searchTimer)
                    this.searching = false
                    if (search.getStatus() !== 0 || !result) {
                        this.error = '未找到结果或搜索暂不可用，请调整关键词重试'
                        return
                    }
                    for (let i = 0; i < result.getCurrentNumPois(); i++) {
                        const item = result.getPoi(i)
                        if (item.point) this.results.push({ title: item.title, address: item.address, point: item.point })
                    }
                    if (!this.results.length) this.error = '未找到门店，请输入更完整的名称或地址'
                },
            })
            this.localSearch = search
            this.searchTimer = setTimeout(() => {
                if (this.disposed || this.localSearch !== search) return
                this.searching = false
                this.error = '搜索暂不可用，请稍后重试'
            }, 15000)
            search.search(this.keywords.trim())
        },
        locationConfirm() {
            if (this.ready && !this.searching && validLocation(this.location)) this.$emit('locationSelected', this.location.slice())
        },
    },
}
</script>
<style scoped>
.location-selector { padding: 20px 0; }
.title { font-size: 20px; padding: 0 20px; }
.search-bar { display: flex; gap: 8px; padding: 16px 20px; }
.map-canvas { height: 360px; border-top: 1px solid #eee; border-bottom: 1px solid #eee; }
.map-message { padding: 10px 20px; color: #a65d00; background: #fff8e6; }
.search-results { max-height: 144px; overflow-y: auto; padding: 0 20px; }
.search-results button { display: block; width: 100%; text-align: left; background: white; border: 0; border-bottom: 1px solid #eee; padding: 8px; cursor: pointer; }
.search-results button:hover { background: #f0f7ff; }
.search-results span { display: block; color: #777; }
.hint { padding: 12px 20px; color: #777; font-size: 12px; }
.foot { display: flex; justify-content: flex-end; gap: 10px; padding: 0 20px; }
</style>



// WEBPACK FOOTER //
// src/components/common/LocationSelector.vue?2968ac6c